import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Modal } from "components/Modal";
import {
  useUpdateFeedbackMutation,
  useUpdateFeedbackSkillMutation,
} from "graphql/__generated__/hooks";
import { useBadgeChecker } from "hooks/useBadgeChecker";
import { VETCOIN_REWARDS, useVetcoin } from "hooks/useVetcoin";
import { RequestFeedbackSteps } from "helpers/constants";
import { RateSelector } from "pages/Feedback/RequestFeedback/Components";
import { BaseSyntheticEvent, FC, useMemo, useState } from "react";
import { FeedbackStatus, FeedbackType } from "types/feedback";
import { EvaluationMetricTable } from "components/EvaluationMetric";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import ConfirmActionModal from "modals/ConfirmActionModal/ConfirmActionModal";
import { useNotifications } from "hooks/useNotifications";
import { NotificationSeverity } from "helpers/constants";
import { useNavigate } from "react-router-dom";
import { AdminRoutes } from "config/routes";
import { CustomLabel } from "types/CustomLabel";
import { Tag } from "../../types/tag";

type CoachReviewProps = {
  feedback: FeedbackType;
  open: boolean;
  onClose: () => void;
  onAddFeedback: () => void;
  customLabels?: CustomLabel[];
};

export const CoachReview: FC<CoachReviewProps> = ({
  feedback,
  open,
  onClose,
  onAddFeedback,
  customLabels = [],
}) => {
  const [updateFeedback, { loading }] = useUpdateFeedbackMutation();
  const [updateFeedbackSkill, { loading: loadingSkill }] =
    useUpdateFeedbackSkillMutation();
  const { checkAndAwardBadge } = useBadgeChecker();
  const { awardVetcoins } = useVetcoin(feedback?.coach?.user_id);

  const [data, setData] = useState<any>({
    coachObserve: "",
    coachRate: 0,
  });

  // When there are multiple competencies (ITER case), allow separate ratings per skill
  const isMultiSkill = feedback.feedback_skills.length > 1;
  const [skillRatings, setSkillRatings] = useState<
    Record<string, number | null>
  >(() =>
    feedback.feedback_skills.reduce(
      (acc, skill) => ({
        ...acc,
        [skill.feedback_skill_id]: skill.coach_rating ?? null,
      }),
      {} as Record<string, number | null>,
    ),
  );

  const [showRubric, setShowRubric] = useState<boolean>(false);
  const [confirmState, setConfirmState] = useState<null | {
    type: "submit" | "decline";
  }>(null);

  const navigate = useNavigate();
  const { notify } = useNotifications();

  const requirement = useMemo(
    () => feedback?.feedback_skills?.[0]?.requirement || null,
    [feedback],
  );

  const onDecline = () => {
    setConfirmState({ type: "decline" });
  };

  const onSubmit = async () => {
    setConfirmState({ type: "submit" });
  };

  const executeAction = async () => {
    if (confirmState?.type === "decline") {
      await updateFeedback({
        variables: {
          id: feedback.feedback_id,
          feedback: {
            status: FeedbackStatus.DECLINED,
          },
        },
      });
    } else if (confirmState?.type === "submit") {
      // Update feedback status to DONE first
      await updateFeedback({
        variables: {
          id: feedback.feedback_id,
          feedback: {
            status: FeedbackStatus.DONE,
          },
        },
      });

      // Apply coach review to all competencies/skills in this feedback
      if (feedback.feedback_skills && feedback.feedback_skills.length > 0) {
        await Promise.all(
          feedback.feedback_skills.map((skill) => {
            const rating = isMultiSkill
              ? skillRatings[skill.feedback_skill_id] ?? data.coachRate
              : data.coachRate;
            return updateFeedbackSkill({
              variables: {
                id: skill.feedback_skill_id,
                feedback_skill: {
                  coach_comments: data.coachObserve,
                  coach_rating: rating,
                },
              },
            });
          }),
        );
      }

      try {
        await awardVetcoins({
          userId: feedback?.coach?.user_id || "",
          amount: VETCOIN_REWARDS.FEEDBACK_EVALUATION_COMPLETED,
          actionType: "FEEDBACK_EVALUATION_COMPLETED",
          sourceFeedbackId: feedback.feedback_id,
          metadata: { flow: "coach_review_submit_approve" },
        });
      } catch (error) {
        console.error("Error awarding vetcoins for coach review:", error);
      }

      // Check for badge eligibility after submitting feedback
      await checkAndAwardBadge();

      onAddFeedback?.();
    }

    onClose();
    notify({
      message:
        confirmState?.type === "submit"
          ? "coach feedback submitted"
          : "coach feedback declined",
      severity: NotificationSeverity.success,
    });
    if (confirmState?.type === "submit") {
      navigate(AdminRoutes.FeedbackSubmissionConfirmation, {
        state: {
          title: "Coach Feedback Successfully Submitted",
          vetcoinsAwarded: VETCOIN_REWARDS.FEEDBACK_EVALUATION_COMPLETED,
        },
      });
      return;
    }
    navigate(AdminRoutes.Home);
  };

  return (
    <Modal open={open} onClose={onClose} title="Coach Feedback">
      <Box>
        <Alert severity="info" sx={{ my: 2 }}>
          <AlertTitle>
            Before approving, please add feedback for the learner.
          </AlertTitle>
        </Alert>
        <Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <Button
              variant={"text"}
              color="primary"
              size="small"
              onClick={() => setShowRubric(!showRubric)}
              startIcon={
                showRubric ? <KeyboardArrowUp /> : <KeyboardArrowDown />
              }
            >
              {showRubric ? "Hide Success Rubric" : "See Success Rubric"}
            </Button>
          </Box>
          {showRubric && requirement && (
            <Box mb={2}>
              <Box p={2}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Success Rubric
                </Typography>
                <EvaluationMetricTable
                  requirement={requirement}
                  evaluationMetrics={requirement.evaluation_metrics || []}
                  customLabels={customLabels}
                />
              </Box>
            </Box>
          )}
          <Typography variant="body1" fontWeight={600} sx={{ my: 1 }}>
            1. {RequestFeedbackSteps.question1ReflectCoachDetail}
            <Typography component={"span"} color="error">
              *
            </Typography>
          </Typography>
          <ul style={{ listStyleType: "disc" }}>
            <li>What did you observe?</li>
            <li>What are some modifiable areas for improvement?</li>
            <li>What are specific actions the learner can take to improve?</li>
            {!!feedback?.tags?.length && feedback?.tags?.length > 0 && (
              <li>
                How was the learner's performance in terms of{" "}
                {feedback?.tags?.map((f: { tag: Tag }) => (
                  <Chip
                    component={"span"}
                    label={f.tag.name}
                    color="primary"
                    variant="filled"
                    size="small"
                    sx={{ mr: 0.5 }}
                  />
                ))}
                ?
              </li>
            )}
          </ul>
          <Box my={2}>
            <TextField
              multiline
              rows={4}
              fullWidth
              onChange={(e: BaseSyntheticEvent) => {
                setData({
                  ...data,
                  coachObserve: e.target.value,
                });
              }}
              value={data.coachObserve}
            />
          </Box>

          <Typography variant="body1" fontWeight={600} sx={{ my: 1 }}>
            2. {RequestFeedbackSteps.question2ReflectCoachDetail}
            <Typography component={"span"} color="error">
              *
            </Typography>
          </Typography>

          {isMultiSkill ? (
            <Box mt={2}>
              {feedback.feedback_skills.map((skill, index) => (
                <Box key={skill.feedback_skill_id} sx={{ mb: 3 }}>
                  <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
                    {skill.requirement?.name}
                  </Typography>
                  {skill.requirement?.description && (
                    <Typography
                      variant="body2"
                      sx={{ mb: 2, color: "text.secondary" }}
                    >
                      {skill.requirement.description}
                    </Typography>
                  )}
                  <RateSelector
                    value={skillRatings[skill.feedback_skill_id] ?? null}
                    onChange={(value: number) => {
                      setSkillRatings((prev) => ({
                        ...prev,
                        [skill.feedback_skill_id]: value,
                      }));
                    }}
                    requirement={skill.requirement}
                    customLabels={customLabels}
                  />
                  {index < feedback.feedback_skills.length - 1 && (
                    <Box mt={3} />
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Box mt={2}>
              <RateSelector
                value={data.coachRate}
                onChange={(value: number) => {
                  setData({
                    ...data,
                    coachRate: value,
                  });
                }}
                requirement={feedback.feedback_skills[0].requirement}
                customLabels={customLabels}
              />
            </Box>
          )}

          <Stack
            direction="row"
            justifyContent="flex-end"
            spacing={2}
            sx={{ mt: 2 }}
          >
            <Button variant="outlined" color="primary" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="contained" color="error" onClick={onDecline}>
              Decline
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={onSubmit}
              disabled={loading || loadingSkill}
            >
              Submit & Approve
            </Button>
          </Stack>
        </Box>
      </Box>
      <ConfirmActionModal
        show={!!confirmState}
        title={
          confirmState?.type === "submit" ? "Confirm Submit" : "Confirm Decline"
        }
        message={
          confirmState?.type === "submit"
            ? "Are you sure you want to submit?"
            : "Are you sure you want to decline?"
        }
        confirmButtonText="Yes"
        declineButtonText="No"
        onConfirm={() => {
          void executeAction();
          setConfirmState(null);
        }}
        onDismiss={() => setConfirmState(null)}
        onDecline={() => setConfirmState(null)}
      />
    </Modal>
  );
};
